import { useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { fetchAdminBusinesses, fetchAdminUsers, updateUserActiveState, type AdminUserRow } from "@/lib/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [businesses, setBusinesses] = useState<Array<{ id: string; name: string }>>([]);
  const [businessFilter, setBusinessFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [userRows, businessRows] = await Promise.all([fetchAdminUsers(), fetchAdminBusinesses()]);
      setUsers(userRows);
      setBusinesses(businessRows.map((row) => ({ id: row.id, name: row.name })));
      setError("");
    } catch (nextError: any) {
      setError(nextError.message ?? "Unable to load users.");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(
    () =>
      users.filter((user) => {
        const matchesBusiness = businessFilter === "all" || user.businessId === businessFilter;
        const haystack = [user.displayName, user.email, user.businessName, user.role].join(" ").toLowerCase();
        return matchesBusiness && haystack.includes(search.toLowerCase());
      }),
    [businessFilter, search, users],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-950">Users</h1>
          <p className="mt-2 text-sm text-slate-600">Audit all platform users, filter by tenant, and deactivate accounts when needed.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={businessFilter}
            onChange={(event) => setBusinessFilter(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="all">All businesses</option>
            <option value="">Platform users</option>
            {businesses.map((business) => (
              <option key={business.id} value={business.id}>{business.name}</option>
            ))}
          </select>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, role" className="w-full sm:w-72" />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <p>{error}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">User</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Business</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {filtered.map((user) => (
                <tr key={user.id}>
                  <td className="px-5 py-4">
                    <div>
                      <p className="font-medium text-slate-950">{user.displayName || "Unnamed user"}</p>
                      <p className="text-slate-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{user.role}</td>
                  <td className="px-5 py-4 text-slate-600">{user.businessName}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${user.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                      {user.isActive ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-500">{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={user.role === "super_admin"}
                      onClick={async () => {
                        await updateUserActiveState(user.id, !user.isActive);
                        await load();
                      }}
                    >
                      {user.role === "super_admin" ? "Protected" : user.isActive ? "Deactivate" : "Reactivate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
